/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import {
	FieldInput,
	Form,
	Icon,
	IFormProps,
	InputAdornment,
	InputButton,
	useStateProp
} from "@adapptio/lib-editor-ui";
import { isBaseScreenResolutionId, usePreviewResolutionContext } from "../../../previewResolution";
import { isNil } from "@adapptio/lib-shared";

import "./PreviewResolutionSettingsForm.scss";

export interface IPreviewResolutionSettingsProps {
	className?: string;
}

const MAX_WIDTH_VALUE = 5000;

export const PreviewResolutionSettingsForm: React.FC<IPreviewResolutionSettingsProps> = ({ className }) => {
	const baseClass = BEM("edt-preview-resolution-settings-form");

	const { previewWidth, manualWidth, setManualWidth, previewResolutionId } = usePreviewResolutionContext();

	const width = isNil(manualWidth) ? previewWidth : manualWidth;

	const [ previewWidthState, setPreviewWidthState ] = useStateProp(width);

	const handleSubmit = useCallback<IFormProps["onSubmit"]>(() => {
		if (width !== previewWidthState) {
			const widthValue = Math.max(0, Math.min(previewWidthState, MAX_WIDTH_VALUE));
			setManualWidth(widthValue);
			setPreviewWidthState(widthValue);
		}
	}, [ previewWidthState, width, setManualWidth ]);

	const handleWidthChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			const newValue = event.target.valueAsNumber;
			setPreviewWidthState(newValue);
		},
		[ setPreviewWidthState ]
	);

	const autoWidth = isNil(manualWidth) && isBaseScreenResolutionId(previewResolutionId);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Form className={baseClass.toClassName()} onSubmit={handleSubmit} noValidate>
			<FieldInput
				className={baseClass.element("input").toClassName()}
				type="number"
				inputMode="numeric"
				value={previewWidthState}
				onChange={handleWidthChange}
				endAdornment={
					<InputAdornment>
						{autoWidth && (
							<InputButton type="button" title="Auto width" disabled>
								Auto
							</InputButton>
						)}
						<InputButton type="button" title="Clear" onClick={() => setManualWidth(null)}>
							<Icon name="mdi/sync" />
						</InputButton>
					</InputAdornment>
				}
			/>
			<button type="submit" hidden />
		</Form>
	);
};
